import React from "react";

/**
 * Pieces shared by ClientLogDetailPage and DeviceLogDetailPage.
 *
 * The two pages are mirror images of one another — same session, opened from
 * the opposite lane — so everything here was previously duplicated verbatim in
 * both files.
 */

export const PAGE_SIZE = 20;

export function formatStarted(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  return `${mm}-${dd} ${hh}:${min}`;
}

/** "10-04 13:22 · 45m", or just the timestamp when no duration is known. */
export function formatStartedWithDuration(startedAt, durationMin) {
  const started = formatStarted(startedAt);
  if (durationMin == null) return started;
  return `${started} · ${durationMin}m`;
}

export function downloadEventsCsv(filename, forEvents) {
  const escape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = [["Timestamp", "Event", "Detail"], ...forEvents.map((e) => [e.ts, e.event, e.detail])];
  const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Page numbers to render: always the first and last, plus a window of one
 * either side of the current page. Non-consecutive neighbours get an ellipsis.
 */
function visiblePages(totalPages, currentPage) {
  return Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1);
}

export function EventsPagination({ currentPage, totalPages, totalItems, pageSize = PAGE_SIZE, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = visiblePages(totalPages, currentPage);

  return (
    <div className="flex items-center justify-between px-5 pb-4 pt-2 border-t border-slate-100">
      <p className="text-xs text-slate-400">
        Showing {(currentPage - 1) * pageSize + 1}&ndash;{Math.min(currentPage * pageSize, totalItems)} of {totalItems}
      </p>
      <div className="flex items-center gap-1">
        {pages.map((p, idx, arr) => (
          <React.Fragment key={p}>
            {idx > 0 && arr[idx - 1] !== p - 1 && (
              <span className="px-1 text-xs text-slate-400">&hellip;</span>
            )}
            <button
              onClick={() => onPageChange(p)}
              className={`min-w-[2rem] text-xs font-semibold px-2 py-1 rounded-md border transition-colors ${
                p === currentPage
                  ? "bg-[#08148C] text-white border-[#08148C]"
                  : "bg-white text-slate-600 border-slate-300 hover:border-[#08148C]"
              }`}
            >
              {p}
            </button>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/** Count for a lane: the real event list if loaded, else the bundle's stored count. */
export function eventCount(realEvents, fallbackCount) {
  return realEvents ? realEvents.length : fallbackCount;
}

/**
 * Derive everything the events table needs for the active lane.
 *
 * `lanes` is keyed by view name ("client" / "device"), so selecting the active
 * lane is a lookup rather than a chain of ternaries in each page component.
 */
export function deriveEventsView({ view, lanes, page }) {
  const lane = lanes[view];
  const usingRealLog = Boolean(lane.real);
  const allEvents = lane.real || lane.fallback;
  const totalPages = usingRealLog ? Math.max(1, Math.ceil(allEvents.length / PAGE_SIZE)) : 1;
  const currentPage = Math.min(page, totalPages);
  const events = usingRealLog
    ? allEvents.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
    : allEvents;
  return { usingRealLog, allEvents, totalPages, currentPage, events, isLoading: lane.loading };
}
